import { useEffect } from 'react'

// Any open modal Radix overlay (Dialog, Sheet, AlertDialog, Select,
// DropdownMenu) renders with `data-state="open"` and one of these roles.
const OVERLAY_OPEN_SELECTOR =
  '[data-state="open"][role="dialog"],' +
  '[data-state="open"][role="alertdialog"],' +
  '[data-state="open"][role="menu"],' +
  '[data-state="open"][role="listbox"]'

/**
 * App-level safety net for a Radix bug: modal overlays lock
 * `body { pointer-events: none }` while open and restore it on close. When one
 * modal layer is opened from another (e.g. a triple-dot menu launching a dialog
 * or flyout), the overlapping locks can leave the body stuck at `none` after
 * everything closes — freezing the whole page until a refresh.
 *
 * This watches `<body>` and clears a stuck lock once no overlay remains open,
 * covering every overlay primitive in one place rather than patching each
 * component. It only ever clears when nothing legitimately owns the lock, so an
 * open overlay is never affected.
 */
export const useBodyPointerEventsGuard = (): void => {
  useEffect(() => {
    const { body } = document
    let frame = 0

    const clearIfStuck = () => {
      if (body.style.pointerEvents !== 'none') return
      if (document.querySelector(OVERLAY_OPEN_SELECTOR)) return
      body.style.pointerEvents = ''
    }

    const scheduleCheck = () => {
      cancelAnimationFrame(frame)
      // Defer two frames so a just-opening overlay has committed its
      // `data-state="open"` before we conclude nothing owns the lock.
      frame = requestAnimationFrame(() => requestAnimationFrame(clearIfStuck))
    }

    const observer = new MutationObserver(() => {
      if (body.style.pointerEvents === 'none') scheduleCheck()
    })

    // attributes: catches Radix writing `pointer-events: none` (incl. the
    // corrupted restore on close). childList: catches overlay portals being
    // removed from <body> on close, which is the reliable "closed" signal.
    observer.observe(body, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
    })

    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [])
}
